// @flow

import { isEmpty, get } from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';

import { getDepartureDate } from 'Store/trip-criteria/tripCriteria.selector';
import { type CalendarState } from 'Store/calendar/calendar.reducer';

const INITIAL_LIST = [];
const INITIAL_CALENDAR = {};

function getMonthIndex(months = [], monthId) {
  return months.findIndex(({ id }) => id === monthId);
}

function getMonthIndexFromDays(months = [], dayId) {
  /**
   * return if a day is elligible based on:
   * - dayContent.id equals dayId parameter
   * - dayContent.isCurrentMonth at true
   * @param  {Object} day  objet day from calendar.months[i]
   * @return {boolean}     this day is elligible
   */
  const matchBasedOnDay = ({ dayContent }) => dayContent.id === dayId && dayContent.isCurrentMonth;
  return months.findIndex(({ days }) => days.some(matchBasedOnDay));
}

function getCurrentMonth(months = [], selectedDay, selectedDayMonthIndex, firstMonthIndex) {
  return selectedDay && selectedDayMonthIndex > -1
    ? months[selectedDayMonthIndex]
    : months[firstMonthIndex];
}

function getDaysFromMonths(calendar, monthId) {
  const { months } = calendar;
  const monthIndex = getMonthIndex(months, monthId);

  return get(months, `${monthIndex}.days`);
}

function getDaysFromDays(calendar, dayId) {
  const { months, firstAvailableMonthIndex } = calendar;
  const selectedDayMonthIndex = getMonthIndexFromDays(months, dayId);
  const month = getCurrentMonth(months, dayId, selectedDayMonthIndex, firstAvailableMonthIndex);

  return get(month, 'days', INITIAL_LIST);
}

function getDaysSelector(calendar = INITIAL_CALENDAR, selectedMonth, selectedDay) {
  if (isEmpty(calendar)) {
    return INITIAL_LIST;
  }

  const daysFromMonths = getDaysFromMonths(calendar, selectedMonth);
  const daysFromDays = getDaysFromDays(calendar, selectedDay);

  return !daysFromMonths || daysFromMonths === daysFromDays ? daysFromDays : daysFromMonths;
}

const calendarSelector = ({ calendar }) => calendar;

export const getCalendar: (
  state: ReduxState,
) => $PropertyType<CalendarState, 'calendar'> = createSelector(
  calendarSelector,
  ({ calendar }: CalendarState) => calendar,
);
export const getSelectedMonth: (
  state: ReduxState,
) => $PropertyType<CalendarState, 'selectedMonth'> = createSelector(
  calendarSelector,
  ({ selectedMonth }: CalendarState) => selectedMonth,
);
export const getCalendarLoading: (
  state: ReduxState,
) => $PropertyType<CalendarState, 'isLoading'> = createSelector(
  calendarSelector,
  ({ isLoading }: CalendarState) => isLoading,
);
export const getDays = createSelector(
  getCalendar,
  getSelectedMonth,
  getDepartureDate,
  getDaysSelector,
);

export const getPricesByDays: (
  state: ReduxState,
) => $PropertyType<CalendarState, 'byDays'> = createSelector(
  calendarSelector,
  ({ byDays }: CalendarState) => byDays,
);
export const getBasePrice: (
  state: ReduxState,
) => $PropertyType<CalendarState, 'basePrice'> | null = createSelector(
  calendarSelector,
  getDepartureDate,
  ({ basePrice, byDays }: CalendarState, departureDate: ?number) => {
    if (!departureDate) {
      return basePrice;
    }

    const dayId = moment(departureDate).format('YYYYMMDD');

    if (!byDays || !byDays[dayId]) {
      return null;
    }

    const { oldPrice, price, unit } = byDays[dayId];

    return {
      bestPrice: price
        ? {
            formattedValue: price,
            value: price,
          }
        : null,
      initialPrice: oldPrice
        ? {
            formattedValue: oldPrice,
            value: oldPrice,
          }
        : null,
      unit,
    };
  },
);
